import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { App } from './App';
import './index.css';
import { AuthProvider } from './lib/auth/AuthContext';
import { ThemeProvider } from './store/theme';
import { StepUpModal } from './components/auth/StepUpModal';
import { reloadIfStaleChunk, startBuildWatch } from './lib/version';

// Register the service worker generated by vite-plugin-pwa. autoUpdate
// means the SW silently swaps in the new version on the next reload —
// no user prompt needed. Wrapped in a try/dynamic-import so dev builds
// (where the virtual module doesn't exist) don't break.
if ('serviceWorker' in navigator) {
  import('virtual:pwa-register')
    .then(({ registerSW }) => registerSW({ immediate: true }))
    .catch(() => { /* no-op in dev */ });
}

// Detect a new deploy while the app (or installed PWA) is already open and
// reload once to pick it up — see src/lib/version.ts.
startBuildWatch();

// A page cached from an earlier deploy asks for code chunks that no longer exist and renders
// blank. Vite reports that as vite:preloadError: reload once to fetch the current build.
// Route chunks loaded with React.lazy fail as an unhandled rejection or into an error boundary.
window.addEventListener('unhandledrejection', (e) => { if (reloadIfStaleChunk(e.reason)) e.preventDefault(); });
window.addEventListener('vite:preloadError', (e) => {
  try {
    if (sessionStorage.getItem('kanvara-chunk-reload') === '1') return;
    sessionStorage.setItem('kanvara-chunk-reload', '1');
  } catch { /* storage blocked: reload anyway */ }
  e.preventDefault();
  window.location.reload();
});
window.addEventListener('load', () => { setTimeout(() => { try { sessionStorage.removeItem('kanvara-chunk-reload'); } catch { /* ignore */ } }, 10_000); });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60_000,  // 3 minutes — don't re-fetch on every nav
      gcTime: 10 * 60_000,    // keep cache 10 minutes after unmount
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <App />
            <StepUpModal />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
